import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Autocomplete, Stack, TextField } from '@mui/material';
import HookFormTextField from 'src/components/hookform/HookFormTextField';
import LoadingButton from 'src/components/LoadingButton';
import BackButton from 'src/components/BackButton';
import { useTagOptions } from 'src/hooks/tag';
import { getDeviceStatusOptions } from 'src/utils/enums/deviceStatus';
import { notesValidation, tagsValidation } from 'src/validations/deviceValidation';

const schema = Yup.object().shape({
  status: Yup.string().required('Status is required'),
  notes: notesValidation,
  tags: tagsValidation.default([]),
});

const toFormTags = items =>
  (items || [])
    .map(item => (typeof item === 'string' ? { name: item.trim() } : item))
    .filter(item => (item?.name || '').trim());

const DeviceBulkStatusForm = ({ isSaving, onCancel, onSubmit }) => {
  const [tagSearch, setTagSearch] = useState('');
  const defaultValues = useMemo(
    () => ({
      status: '',
      notes: '',
      tags: [],
    }),
    [],
  );

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });

  const { control, handleSubmit, reset } = formMethods;
  const { data: tagOptionsData, isLoading: tagOptionsLoading } = useTagOptions({ search: tagSearch });
  const tagOptions = useMemo(() => tagOptionsData?.tags || [], [tagOptionsData]);

  useEffect(() => {
    reset(defaultValues);
    setTagSearch('');
  }, [defaultValues, reset]);

  const submit = async values => {
    await onSubmit({
      status: values.status,
      notes: values.notes?.trim() || '',
      tags: values.tags || [],
    });
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit(submit)}>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <HookFormTextField
          name="status"
          label="Status"
          formMethods={formMethods}
          select
          size="small"
          fullWidth
          options={getDeviceStatusOptions()}
        />
        <HookFormTextField
          name="notes"
          label="Notes"
          formMethods={formMethods}
          placeholder="Optional note"
          size="small"
          multiline
          minRows={4}
          fullWidth
        />
        <Controller
          name="tags"
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              multiple
              freeSolo
              options={tagOptions}
              loading={tagOptionsLoading}
              value={field.value || []}
              onChange={(_, newValue) => {
                field.onChange(toFormTags(newValue));
              }}
              onInputChange={(_, newInput, reason) => {
                if (reason === 'input') {
                  setTagSearch(newInput);
                }
              }}
              getOptionLabel={option => (typeof option === 'string' ? option : option?.name || '')}
              isOptionEqualToValue={(option, selected) => option?.id === selected?.id}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Add Tags"
                  placeholder="Add tags to the selected device(s)"
                  size="small"
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <BackButton disabled={isSaving} onClick={onCancel}>
            Cancel
          </BackButton>
          <LoadingButton type="submit" disabled={isSaving} loading={isSaving}>
            Update Status
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};

DeviceBulkStatusForm.propTypes = {
  isSaving: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default DeviceBulkStatusForm;
